import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'

/**
 * Ofrece instalar la PWA cuando el navegador lo permite.
 *
 * Captura el evento `beforeinstallprompt` (solo lo disparan navegadores
 * basados en Chromium: Chrome/Edge en Android y escritorio) y muestra un
 * toast con un botón "Instalar" que lanza el diálogo nativo del navegador.
 *
 * Importante: iOS/Safari NO dispara este evento; en iPhone la instalación
 * es siempre manual (Compartir → Agregar a inicio), así que en iOS este
 * componente no muestra nada.
 *
 * No renderiza nada; usa el <Toaster /> que ya está en App.jsx.
 */
export default function InstallPrompt() {
  const deferredRef = useRef(null)

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Evita el mini-aviso por defecto de Chrome para mostrar el nuestro.
      e.preventDefault()
      deferredRef.current = e

      toast(
        (t) => (
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Install CRM Kickoff on your device?</span>
            <button
              onClick={async () => {
                toast.dismiss(t.id)
                const promptEvent = deferredRef.current
                if (!promptEvent) return
                promptEvent.prompt()
                await promptEvent.userChoice
                deferredRef.current = null
              }}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '6px 12px',
                background: '#8152ff',
                color: '#fff',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Install
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              style={{
                border: 'none',
                background: 'transparent',
                color: 'inherit',
                fontFamily: 'DM Sans, sans-serif',
                fontSize: 13,
                opacity: 0.7,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Not now
            </button>
          </span>
        ),
        { id: 'pwa-install', duration: Infinity }
      )
    }

    const onInstalled = () => {
      deferredRef.current = null
      toast.dismiss('pwa-install')
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  return null
}
